import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Camera,
  Sparkle,
  Package,
  MegaphoneSimple,
  ShareNetwork,
  CreditCard,
  ChartLine,
  CheckCircle,
  ArrowRight,
  Lightning,
  Brain,
  Globe,
  Buildings,
  Receipt,
  EnvelopeSimple,
  Rocket,
  Play,
  CaretRight,
} from "@phosphor-icons/react";

const VENTAJAS = [
  {
    icon: Brain,
    title: "Decisiones con IA",
    body: "GPT-5.2 Vision identifica tu producto y te dice cuánto vale como nuevo, usado o de oportunidad — antes de que lo compres o vendas.",
  },
  {
    icon: Lightning,
    title: "De foto a venta en minutos",
    body: "Toma la foto, la IA hace el resto: precios, descripción, post para redes, página de pago y cobro por Stripe o WhatsApp.",
  },
  {
    icon: Globe,
    title: "Vende sin tienda física",
    body: "Cada producto tiene su propia página pública con checkout. Comparte el link en cualquier red y el cliente compra directo.",
  },
  {
    icon: Buildings,
    title: "Multi-sucursal con control",
    body: "Cada cajero ve solo SU sucursal. Tú como admin ves el negocio completo y puedes comparar desempeño entre puntos de venta.",
  },
];

const FLOW_STEPS = [
  {
    n: 1,
    icon: Camera,
    title: "Escanea con AI",
    desc: "Toma fotos del producto (o varias). Opcional: dicta detalles por voz. La IA recibe imagen + contexto.",
    cta: "Abrir escáner",
    to: "/escanear",
    color: "klein",
  },
  {
    n: 2,
    icon: Sparkle,
    title: "Identifica y precia",
    desc: "GPT-5.2 reconoce el objeto, su categoría, estado y te sugiere 3 precios: nuevo, usado, oportunidad — basado en mercado.",
    cta: "Ver ejemplo",
    to: "/escanear",
    color: "klein",
  },
  {
    n: 3,
    icon: Package,
    title: "Guarda en inventario",
    desc: "Un click y el producto entra a tu catálogo con foto mejorada (Gemini Nano Banana), costo, precio y utilidad.",
    cta: "Ver inventario",
    to: "/inventario",
    color: "klein",
  },
  {
    n: 4,
    icon: MegaphoneSimple,
    title: "Genera publicación",
    desc: "La IA escribe título, copy y hashtags adaptados a la red social. Predice score de desempeño.",
    cta: "Crear post",
    to: "/publicaciones",
    color: "klein",
  },
  {
    n: 5,
    icon: ShareNetwork,
    title: "Comparte el link",
    desc: "Cada producto tiene página pública con galería, descripción y carrito. WhatsApp, Instagram, TikTok, email — donde sea.",
    cta: "Ver páginas",
    to: "/inventario",
    color: "klein",
  },
  {
    n: 6,
    icon: CreditCard,
    title: "Cobra en automático",
    desc: "Stripe (tarjeta), WhatsApp (manual con cotización) o efectivo en tienda. Quedan registrados como pedidos.",
    cta: "Ver pedidos",
    to: "/ventas",
    color: "klein",
  },
  {
    n: 7,
    icon: ChartLine,
    title: "Mide y mejora",
    desc: "Corte del día, gráficas semanal/mensual/anual, comparativa por sucursal o cajero, exportar/importar Excel.",
    cta: "Ver reportes",
    to: "/reportes",
    color: "klein",
  },
];

const QUICK_ACTIONS = [
  { label: "Escanear producto nuevo", icon: Camera, to: "/escanear", help: "Inicia con una foto: la IA hace todo lo demás." },
  { label: "Crear publicación", icon: MegaphoneSimple, to: "/publicaciones", help: "Genera copy + hashtags para redes." },
  { label: "Ver corte del día", icon: ChartLine, to: "/reportes", help: "Resumen completo de hoy." },
  { label: "Pedidos pendientes", icon: Receipt, to: "/ventas", help: "Marca como pagado, enviado o cancelado." },
  { label: "Capturar suscriptor", icon: EnvelopeSimple, to: "/suscriptores", help: "Lista de emails para campañas." },
  { label: "Códigos de referido", icon: ShareNetwork, to: "/referidos", help: "10% de descuento por referido." },
];

export default function Guia() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tourStep, setTourStep] = useState(0);
  const inTour = tourStep > 0;

  const startTour = () => {
    setTourStep(1);
    setTimeout(() => {
      const el = document.getElementById("paso-1");
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const advance = () => {
    if (tourStep < FLOW_STEPS.length) {
      const next = tourStep + 1;
      setTourStep(next);
      setTimeout(() => {
        const el = document.getElementById(`paso-${next}`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    } else {
      setTourStep(0);
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-8 max-w-[1400px] mx-auto" data-testid="guia-page">
      {/* HERO */}
      <header className="border-b border-zinc-200 pb-12 mb-12">
        <div className="overline text-klein">Cómo funciona</div>
        <h1 className="h-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mt-2 leading-[0.95]">
          De una foto a una venta,<br />
          <span className="text-klein">sin pasos extra.</span>
        </h1>
        <p className="text-base text-muted2 mt-6 max-w-2xl leading-relaxed">
          VendeIA es tu plataforma todo-en-uno para reseller, tienda local o emprendimiento online.
          Toma una foto del producto, la inteligencia artificial lo identifica, calcula precios,
          arma la publicación, abre tu página pública con cobro y te entrega el corte del día —
          todo desde el mismo panel.
          {user?.role === "admin" && (
            <> Como admin tienes control total: sucursales, usuarios, exportar/importar Excel y comparativas entre puntos de venta.</>
          )}
        </p>
        <div className="flex flex-wrap gap-3 mt-8">
          <button
            onClick={startTour}
            data-testid="btn-start-tour"
            data-help="Recorre los 7 pasos del flujo en orden, con destacados visuales en cada uno."
            className="bg-klein text-white px-6 py-3 text-sm font-medium hover:bg-klein/90 inline-flex items-center gap-2"
          >
            <Play size={16} weight="fill" /> Empezar tour guiado
          </button>
          <Link
            to="/escanear"
            data-testid="btn-direct-scan"
            data-help="Salta directo al primer paso: escanear un producto."
            className="border border-zinc-300 px-6 py-3 text-sm hover:bg-zinc-50 inline-flex items-center gap-2"
          >
            Ir directo a escanear <ArrowRight size={16} />
          </Link>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/guia-completa.pdf`}
            download
            data-testid="btn-download-guide-pdf"
            data-help="Descarga el manual completo en PDF (con portada, índice clickeable y diagramas)."
            className="border border-klein/30 bg-klein/5 text-klein px-6 py-3 text-sm hover:bg-klein/10 inline-flex items-center gap-2 font-semibold"
          >
            📘 Descargar guía (PDF)
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/guia-completa.download`}
            download
            data-testid="btn-download-guide-md"
            data-help="Descarga la versión markdown (editable, ideal para GitHub o Notion)."
            className="text-xs text-muted2 hover:text-klein underline inline-flex items-center gap-1 ml-2"
          >
            o versión .md
          </a>
        </div>
      </header>

      {/* VENTAJAS */}
      <section className="mb-16" data-testid="ventajas-section">
        <div className="overline text-klein mb-3">Ventajas</div>
        <h2 className="h-display text-3xl font-bold tracking-tight mb-8">
          Por qué tener VendeIA en tu negocio
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-zinc-200">
          {VENTAJAS.map((v) => (
            <div
              key={v.title}
              className="border-r border-b border-zinc-200 p-6"
              data-testid={`ventaja-${v.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <v.icon size={28} className="text-klein" weight="duotone" />
              <div className="text-base font-semibold mt-4">{v.title}</div>
              <div className="text-sm text-muted2 mt-2 leading-relaxed">{v.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FLOW DIAGRAM */}
      <section className="mb-16">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="overline text-klein mb-2">Flujo completo</div>
            <h2 className="h-display text-3xl font-bold tracking-tight">
              7 pasos: de la cámara al cobro
            </h2>
          </div>
          {inTour && (
            <div className="flex items-center gap-3" data-testid="tour-progress">
              <span className="text-xs text-muted2 font-mono">
                Paso {tourStep} de {FLOW_STEPS.length}
              </span>
              <button
                onClick={() => setTourStep(0)}
                className="text-xs text-muted2 underline"
                data-testid="btn-tour-exit"
              >
                Salir del tour
              </button>
            </div>
          )}
        </div>

        <ol className="space-y-3">
          {FLOW_STEPS.map((step, i) => {
            const active = inTour && tourStep === step.n;
            const passed = inTour && tourStep > step.n;
            return (
              <li
                key={step.n}
                id={`paso-${step.n}`}
                data-testid={`flow-step-${step.n}`}
                className={`grid grid-cols-12 gap-0 border transition-all duration-300 ${
                  active
                    ? "border-klein shadow-[0_0_0_3px_rgba(0,47,167,0.12)] bg-klein/[0.02]"
                    : passed
                    ? "border-emerald-200 bg-emerald-50/30"
                    : "border-zinc-200 hover:border-zinc-400"
                }`}
              >
                {/* Number column */}
                <div
                  className={`col-span-2 sm:col-span-1 flex items-center justify-center border-r ${
                    active
                      ? "border-klein bg-klein text-white"
                      : passed
                      ? "border-emerald-200 bg-emerald-100 text-emerald-700"
                      : "border-zinc-200 bg-zinc-50 text-zinc-400"
                  }`}
                >
                  {passed ? (
                    <CheckCircle size={24} weight="fill" />
                  ) : (
                    <span className="h-display text-3xl font-black tracking-tighter">
                      {String(step.n).padStart(2, "0")}
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="col-span-10 sm:col-span-11 grid grid-cols-1 sm:grid-cols-12 gap-4 p-5 sm:p-6 items-center">
                  <div className="sm:col-span-1 flex justify-start sm:justify-center">
                    <step.icon
                      size={28}
                      weight={active ? "fill" : "regular"}
                      className={active ? "text-klein" : "text-ink"}
                    />
                  </div>
                  <div className="sm:col-span-7">
                    <div className="text-base font-semibold">{step.title}</div>
                    <div className="text-sm text-muted2 mt-1 leading-relaxed">
                      {step.desc}
                    </div>
                  </div>
                  <div className="sm:col-span-4 flex flex-wrap gap-2 sm:justify-end">
                    <Link
                      to={step.to}
                      data-testid={`flow-step-${step.n}-cta`}
                      data-help={`Ve directo al paso ${step.n}: ${step.title.toLowerCase()}.`}
                      className={`px-4 py-2 text-sm inline-flex items-center gap-2 border transition-colors ${
                        active
                          ? "bg-klein text-white border-klein hover:bg-klein/90"
                          : "border-zinc-300 hover:bg-zinc-50"
                      }`}
                    >
                      {step.cta} <CaretRight size={14} />
                    </Link>
                    {active && (
                      <button
                        onClick={advance}
                        data-testid={`flow-step-${step.n}-next`}
                        className="bg-ink text-white px-4 py-2 text-sm hover:bg-ink/90 inline-flex items-center gap-2"
                      >
                        {tourStep === FLOW_STEPS.length ? "Terminar tour" : "Siguiente paso"}
                        <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      {/* QUICK ACTIONS */}
      <section className="mb-16" data-testid="quick-actions-section">
        <div className="overline text-klein mb-3">Atajos</div>
        <h2 className="h-display text-3xl font-bold tracking-tight mb-8">
          Acciones rápidas
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.to}
              onClick={() => nav(a.to)}
              data-testid={`qa-${a.to.replace("/", "")}`}
              data-help={a.help}
              className="group border border-zinc-200 p-5 text-left hover:border-klein hover:bg-klein/[0.02] transition-colors flex items-center gap-4"
            >
              <a.icon size={26} className="text-klein shrink-0" weight="duotone" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{a.label}</div>
                <div className="text-xs text-muted2 mt-0.5 truncate">{a.help}</div>
              </div>
              <ArrowRight size={16} className="text-muted2 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </section>

      {/* TIP / CIERRE */}
      <section
        className="border border-zinc-200 bg-zinc-50 p-8 sm:p-10 grid grid-cols-1 md:grid-cols-3 gap-6"
        data-testid="tip-section"
      >
        <div>
          <Rocket size={32} className="text-klein" weight="duotone" />
          <div className="overline text-klein mt-3">Tip</div>
          <div className="h-display text-2xl font-bold tracking-tight mt-2">
            ¿Primera vez?
          </div>
        </div>
        <div className="md:col-span-2 text-sm leading-relaxed text-ink/80">
          Empieza con un solo producto: tómale 2 fotos, deja que la IA lo precie, guárdalo y
          genera una publicación. En 5 minutos tendrás listo el flujo completo desde catálogo
          hasta cobro. Después escala: agrega sucursales, sube cajeros y exporta tu base
          cuando quieras.
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/escanear"
              data-testid="tip-cta-scan"
              className="bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 inline-flex items-center gap-2"
            >
              <Camera size={16} /> Empezar ahora
            </Link>
            <Link
              to="/reportes"
              data-testid="tip-cta-reports"
              className="border border-zinc-300 px-5 py-2.5 text-sm hover:bg-white inline-flex items-center gap-2"
            >
              <ChartLine size={16} /> Ver reportes
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
